import {
  type AnyFn,
  type MaybeRefOrGetter,
  StorageSerializers,
  type UseStorageOptions,
  createSharedComposable,
  useLocalStorage,
} from '@vueuse/core'
import { type Pinia, type StateTree, createPinia } from 'pinia'

import { isSSR } from '@fond-of/env'

declare global {
  interface Window {
    __pinia?: Record<string, StateTree>
  }
}

let globalStore: Pinia

// @ts-expect-error
let asyncLocalStorage

if (isSSR() && import.meta.env.SSR) {
  ;({ asyncLocalStorage } = await import('internal/astro/state'))
}

export const getStore = () => {
  if (isSSR() && import.meta.env.SSR) {
    // @ts-expect-error
    const serverStore = asyncLocalStorage?.getStore()
    if (!serverStore) {
      return createPinia()
    }
    return serverStore.get('pinia')
  }
  if (!globalStore) {
    initStore()
    if (!isSSR()) {
      hydrateStore()
    }
  }
  return globalStore
}

export const initStore = () => {
  globalStore = createPinia()
}

export const hydrateStore = () => {
  if (!window.__pinia) {
    return
  }

  globalStore.state.value = window.__pinia

  document.getElementById('pinia')?.remove()
  delete window.__pinia
}

export const useLocalStorageObject = <T extends object | null>(
  key: string,
  initialValue: MaybeRefOrGetter<T>,
  options?: Omit<UseStorageOptions<T>, 'serializer'>
) =>
  useLocalStorage<T>(key, initialValue, {
    ...options,
    serializer: StorageSerializers.object,
  })

export const createClientSharedComposable = <Fn extends AnyFn>(
  composable: Fn
) => (isSSR() ? composable : createSharedComposable(composable))
